import React from 'react';
import { Box, Button, Chip, Paper, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { brandPalette } from '../../utils/brandPalette';

export const workspacePalette = {
  ...brandPalette,
  primary: 'var(--primary,#2E6F8E)',
  secondary: 'var(--secondary,#42B89B)',
  accent: 'var(--accent,#7C5CF2)',
};

export const alpha = (color, opacity = 1) => {
  const a = Math.max(0, Math.min(1, Number(opacity) || 0));
  if (String(color).startsWith('var(')) {
    return `color-mix(in srgb, ${color} ${Math.round(a * 100)}%, transparent)`;
  }
  const value = String(color || '#000000').replace('#', '');
  if (/^[0-9a-f]{6}$/i.test(value)) {
    const r = parseInt(value.slice(0, 2), 16);
    const g = parseInt(value.slice(2, 4), 16);
    const b = parseInt(value.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  }
  return color;
};

export const panelSx = (accent = workspacePalette.primary, extra = {}) => ({
  position: 'relative',
  minWidth: 0,
  overflow: 'hidden',
  borderRadius: '16px',
  background: `linear-gradient(145deg, rgba(255,255,255,.96), ${alpha(accent, .035)})`,
  backdropFilter: 'blur(16px) saturate(130%)',
  WebkitBackdropFilter: 'blur(16px) saturate(130%)',
  border: `1px solid ${alpha(accent, .11)}`,
  boxShadow: `0 7px 20px rgba(25,57,78,.07), inset 0 1px 0 rgba(255,255,255,.90)`,
  transition: 'transform .22s ease, box-shadow .22s ease, border-color .22s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    right: -44,
    bottom: -56,
    width: 160,
    height: 160,
    borderRadius: '50%',
    background: `radial-gradient(circle at 35% 35%, ${alpha(accent, .14)}, transparent 68%)`,
    pointerEvents: 'none',
  },
  ...extra,
});

export const tableContainerSx = {
  borderRadius: '16px',
  border: `1px solid ${workspacePalette.hairline}`,
  overflowX: 'auto',
  boxShadow: '0 8px 22px rgba(25,57,78,.045)',
  '& .MuiTableHead-root': { background: '#F7FAFC' },
  '& .MuiTableCell-head': {
    color: workspacePalette.inkLight,
    fontSize: '.62rem',
    fontWeight: 900,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableCell-body': { color: '#334056', fontSize: '.73rem' },
  '& .MuiTableRow-root:hover': { backgroundColor: 'rgba(47,111,237,.025)' },
};

export const dialogPaperSx = {
  borderRadius: '20px',
  border: `1px solid ${workspacePalette.hairline}`,
  boxShadow: '0 30px 80px rgba(18,45,64,.22)',
  overflow: 'hidden',
};

export const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '11px',
    background: '#fff',
    '&:hover fieldset': { borderColor: alpha(workspacePalette.primary, .35) },
    '&.Mui-focused fieldset': { borderColor: workspacePalette.primary },
  },
  '& .MuiInputLabel-root': { color: workspacePalette.inkLight, fontWeight: 600 },
};

export const primaryButtonSx = {
  borderRadius: '11px',
  textTransform: 'none',
  fontWeight: 800,
  px: 2,
  color: 'var(--brand-action-text,#fff)',
  background: `linear-gradient(135deg, ${workspacePalette.primary}, ${workspacePalette.secondary})`,
  boxShadow: `0 9px 22px ${alpha(workspacePalette.primary, .2)}`,
  '&:hover': {
    background: `linear-gradient(135deg, ${workspacePalette.primary}, ${workspacePalette.secondary})`,
    boxShadow: `0 13px 30px ${alpha(workspacePalette.primary, .27)}`,
    transform: 'translateY(-1px)',
  },
};

export const quietButtonSx = {
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 750,
  color: workspacePalette.inkSoft,
  borderColor: workspacePalette.hairlineStrong,
  background: '#fff',
  '&:hover': { borderColor: alpha(workspacePalette.primary, .28), background: alpha(workspacePalette.primary, .035) },
};

export function WorkspacePage({ children, maxWidth = 1520, sx = {} }) {
  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 94px)',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        position: 'relative',
        background: 'transparent',
        overflow: 'visible',
        ...sx,
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth, mx: 'auto', p: { xs: .25, sm: .5, md: .75 } }}>
        {children}
      </Box>
    </Box>
  );
}

export function WorkspaceHeader({ eyebrow = 'TELCOFAIR', title, subtitle, icon, action, accent = workspacePalette.primary }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, flexWrap: 'wrap', mb: 1.7 }}>
      <Box sx={{ display: 'flex', gap: 1.1, alignItems: 'center', minWidth: 0 }}>
        {icon && (
          <Box sx={{ width: 46, height: 46, flexShrink: 0, display: 'grid', placeItems: 'center', borderRadius: '14px', color: 'var(--primary-contrast,#fff)', background: `linear-gradient(145deg, ${accent}, ${alpha(accent, .72)})`, border: '1px solid rgba(255,255,255,.55)', boxShadow: `0 11px 24px ${alpha(accent, .26)}`, '& svg': { fontSize: 22 } }}>
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '.58rem', fontWeight: 900, letterSpacing: '.13em', color: workspacePalette.inkFaint, textTransform: 'uppercase', lineHeight: 1.1 }}>{eyebrow}</Typography>
          <Typography sx={{ color: workspacePalette.ink, fontWeight: 850, fontSize: { xs: '1.45rem', md: '1.72rem' }, letterSpacing: '-.035em', lineHeight: 1.13, mt: .25 }}>{title}</Typography>
          {subtitle && <Typography sx={{ color: workspacePalette.inkSoft, fontSize: { xs: '.73rem', md: '.79rem' }, mt: .35, maxWidth: 820, lineHeight: 1.55 }}>{subtitle}</Typography>}
        </Box>
      </Box>
      {action && <Box sx={{ display: 'flex', gap: .8, alignItems: 'center', flexWrap: 'wrap' }}>{action}</Box>}
    </Box>
  );
}

export function WorkspaceStatCard({ label, value, hint, icon, accent = workspacePalette.blue, delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .28, delay }} style={{ height: '100%', minWidth: 0 }}>
      <Paper elevation={0} sx={panelSx(accent, { p: 1.45, minHeight: 112, height: '100%', display: 'flex', gap: 1.05, alignItems: 'flex-start' })}>
        <Box sx={{ position: 'absolute', right: 8, bottom: -16, opacity: .055, color: accent, '& svg': { fontSize: 82 }, transform: 'rotate(-8deg)' }}>{icon}</Box>
        <Box sx={{ width: 43, height: 43, flexShrink: 0, borderRadius: '13px', display: 'grid', placeItems: 'center', color: '#fff', background: `linear-gradient(145deg, ${accent}, ${alpha(accent, .72)})`, boxShadow: `0 10px 22px ${alpha(accent, .25)}`, position: 'relative', zIndex: 1, '& svg': { fontSize: 20 } }}>{icon}</Box>
        <Box sx={{ minWidth: 0, position: 'relative', zIndex: 1 }}>
          <Typography sx={{ fontSize: '.58rem', fontWeight: 900, letterSpacing: '.07em', color: workspacePalette.inkSoft, textTransform: 'uppercase' }}>{label}</Typography>
          <Typography sx={{ mt: .15, color: workspacePalette.ink, fontWeight: 880, fontSize: { xs: '1.42rem', md: '1.65rem' }, lineHeight: 1.08, letterSpacing: '-.04em', wordBreak: 'break-word' }}>{value}</Typography>
          <Box sx={{ mt: .65, display: 'flex', alignItems: 'center', gap: .5 }}><Box sx={{ width: 5.5, height: 5.5, borderRadius: '50%', bgcolor: accent, boxShadow: `0 0 0 4px ${alpha(accent,.09)}` }} /><Typography sx={{ fontSize: '.56rem', color: workspacePalette.inkLight, fontWeight: 600 }} noWrap>{hint || 'Live platform data'}</Typography></Box>
        </Box>
        <Box sx={{ position: 'absolute', left: 0, bottom: 0, height: 3, width: '70%', background: `linear-gradient(90deg, ${accent}, transparent)` }} />
      </Paper>
    </motion.div>
  );
}

export function WorkspacePanel({ children, accent = workspacePalette.primary, sx = {}, hover = false }) {
  return (
    <Paper elevation={0} sx={panelSx(accent, { p: { xs: 1.35, md: 1.6 }, ...(hover ? { transition: 'transform .2s ease, box-shadow .2s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 16px 34px ${alpha(accent,.11)}, 0 4px 12px rgba(25,57,78,.05)` } } : {}), ...sx })}>
      <Box sx={{ position: 'relative', zIndex: 1, minWidth: 0 }}>{children}</Box>
    </Paper>
  );
}

export function PanelTitle({ title, subtitle, icon, accent = workspacePalette.primary, action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.15 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, minWidth: 0 }}>
        {icon && <Box sx={{ width: 34, height: 34, borderRadius: '10px', display: 'grid', placeItems: 'center', color: '#fff', background: `linear-gradient(145deg, ${accent}, ${alpha(accent,.72)})`, boxShadow: `0 8px 18px ${alpha(accent,.22)}`, '& svg': { fontSize: 17 } }}>{icon}</Box>}
        <Box sx={{ minWidth: 0 }}><Typography sx={{ color: workspacePalette.ink, fontSize: '.84rem', fontWeight: 820, lineHeight: 1.2 }}>{title}</Typography>{subtitle && <Typography sx={{ color: workspacePalette.inkLight, fontSize: '.62rem', mt: .2 }}>{subtitle}</Typography>}<Box sx={{ width: 34, height: 2, borderRadius: 10, bgcolor: accent, opacity: .5, mt: .45 }} /></Box>
      </Box>
      {action}
    </Box>
  );
}

export function WorkspaceEmpty({ icon, title, text, actionLabel, onAction, accent = workspacePalette.primary }) {
  return (
    <Box sx={{ minHeight: 210, display: 'grid', placeItems: 'center', textAlign: 'center', px: 2, py: 3 }}>
      <Box>
        <Box sx={{ width: 52, height: 52, mx: 'auto', mb: 1.1, borderRadius: '16px', display: 'grid', placeItems: 'center', color: accent, bgcolor: alpha(accent,.08), border: `1px solid ${alpha(accent,.12)}`, '& svg': { fontSize: 25 } }}>{icon}</Box>
        <Typography sx={{ color: workspacePalette.ink, fontWeight: 820, fontSize: '.9rem' }}>{title}</Typography>
        <Typography sx={{ color: workspacePalette.inkLight, fontSize: '.7rem', mt: .45, maxWidth: 430 }}>{text}</Typography>
        {actionLabel && <Button onClick={onAction} sx={{ ...primaryButtonSx, mt: 1.4 }} size="small">{actionLabel}</Button>}
      </Box>
    </Box>
  );
}

export function StatusChip({ label, color = workspacePalette.success, icon }) {
  return <Chip size="small" icon={icon} label={label} sx={{ bgcolor: alpha(color,.09), color, border: `1px solid ${alpha(color,.15)}`, fontWeight: 850, '& .MuiChip-icon': { color } }} />;
}
